import angular from 'angular';

import ReportTableColumns from '../../reports/services/ReportTableColumns';
import { ROSTER_COLUMN_DESCRIPTIONS } from '../constants';
import config from '../../config';

import rosterUrl from './rosterComponent.html';

const ROSTER_COLUMNS_PROPERTY = 'rosterColumns';

class RosterCtrl {
  isLoading = true;

  constructor(
    Authentication,
    TimesheetsService,
    ShiftUtils,
    EventCollection,
    PersonaCollection,
    DayPartsService,
    RosterService,
    RosterUtils,
    Utils,
    dateFilter,
    orderByFilter,
    sdk,
    rosterPrintPayloadReplacer,
    $scope
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      TimesheetsService,
      ShiftUtils,
      EventCollection,
      PersonaCollection,
      DayPartsService,
      RosterService,
      RosterUtils,
      Utils,
      dateFilter,
      orderByFilter,
      sdk,
      rosterPrintPayloadReplacer,
      $scope,
    });

    this.processingMode = sdk('constants').PROCESSING_MODES.USER_SEPARATED;
    this.EVENT_STATUS = sdk('constants').EVENT_STATUS;
    this.DISPLAY_MODES = sdk('constants').DISPLAY_MODES;
  }

  $onInit() {
    if (!__HEADLESS__) {
      this.RosterService.init();
      this.initGroups();
      this.initTableColumns();

      this.DISABLING_SCHEDULE_AUTOSYNC_ENABLED = Array.isArray(
        config.DISABLING_SCHEDULE_AUTOSYNC_ENABLED
      )
        ? config.DISABLING_SCHEDULE_AUTOSYNC_ENABLED.includes(this.Authentication.user.org.id)
        : config.DISABLING_SCHEDULE_AUTOSYNC_ENABLED;
      this.FORCE_SCHEDULE_AUTOSYNC_OFF = config.FORCE_SCHEDULE_AUTOSYNC_OFF;
      this.setRefreshWatch();
    }

    this.$scope.$emit('RosterCtrl:loaded', this);
  }

  $onDestroy() {
    this.TimesheetsService.leaveSocketRoom();
  }

  get dateString() {
    return this.Utils.formatDate(this.RosterService.date);
  }

  setRefreshWatch() {
    this.TimesheetsService.joinSocketRoom();
    this.$scope.$on('socket:timesheets:sync', () => {
      if (
        this.DISABLING_SCHEDULE_AUTOSYNC_ENABLED &&
        (this.Authentication.user.settings.scheduleAutoSync === false ||
          this.FORCE_SCHEDULE_AUTOSYNC_OFF)
      ) {
        this.rosterHasPendingChanges = true;
      } else {
        this.Utils.debounce(this.refresh, 1000);
      }
    });
  }

  get printHeaderText() {
    const isToday = this.dateString === this.Utils.formatDate(new Date());
    const header = this.dateFilter(this.RosterService.date, 'EEE • dd MMMM');
    const suffix = this.Utils.formatTime(
      this.RosterService.date,
      this.Authentication.user.settings.usTimeFormat
    );

    return !isToday ? header : `${header} ${suffix}`;
  }

  get hasEvents() {
    return Boolean(this.events?.length);
  }

  refresh = () => {
    this.isLoading = true;
    this.initGroups();
  };

  refreshSilently = () => {
    this.initGroups();
  };

  getDataForExport() {
    const columns = this.columns.reportColumns.filter((c) => c.active && !this.shouldHideColumn(c));
    const data = this.orderByFilter(this.RosterService.tableData, [
      'sortKey',
      'isGroupHeader',
      this.RosterService.tableSortChain,
    ]);

    const dataForExport = {
      header: columns.map((c) => ROSTER_COLUMN_DESCRIPTIONS[c.property]?.toUpperCase()),
      body: data.map((item) => columns.map((c) => this._mapItemField(item, c.property))),
    };

    return dataForExport;
  }

  getGroupName(group) {
    return this.ShiftUtils.getGroupTitle(group);
  }

  clearAllFilters() {
    this.RosterService.clearAllFilters();
    this.refreshGroups();
  }

  initTableColumns() {
    this.columns = new ReportTableColumns(this.Authentication, ROSTER_COLUMNS_PROPERTY);
    this.columns.reset();
  }

  setGroups(processedEvents) {
    const groups = { ...processedEvents.groups };

    delete groups.available;
    delete groups.unassigned;

    this.groups = groups;
  }

  async fetchData() {
    return this.RosterService.getRosterReport(this.dateString);
  }

  async initGroups() {
    const { events, hasShiftsWithinTwoWeeks } = await this.fetchData();
    this.hasShiftsWithinTwoWeeks = hasShiftsWithinTwoWeeks;
    this.rosterHasPendingChanges = false;
    this.events = events.filter(
      this.sdk('process/filterOutputByModeAndDate')(this.DISPLAY_MODES.DAY, this.dateString)
    );

    this.eventUsers = angular.copy(this.EventCollection.allUsers);
    await this.DayPartsService.loadData();
    const processedEvents = await this._processEvents();
    this.setGroups(processedEvents);
    this.isLoading = false;
  }

  async refreshGroups() {
    const processedEvents = await this._processEvents();

    this.setGroups(processedEvents);
    this.$scope.$evalAsync();
  }

  shouldHideColumn(column) {
    const isHiddenByPremium = !this.Authentication.user.org.isPremium && column.isPremium;
    return (
      isHiddenByPremium ||
      (column.property === 'tags' && !this.RosterService.tags.length) ||
      (column.property === 'groups' && !this.RosterService.userGroups.length) ||
      (column.property === 'status' && this._isStatusColumnHidden())
    );
  }

  _isStatusColumnHidden() {
    return (
      !this.Authentication.user.orgSettings.clockInEnabled &&
      !this.Authentication.isOrgFeatureEnabled('noShow') &&
      !this.Authentication.isOrgFeatureEnabled('sickCallout')
    );
  }

  _mapItemField(item, field) {
    if (item.isGroupHeader) {
      return field === 'employees' ? this.getGroupName(item) : '';
    }

    const groups = item.user.groups;
    const tags = item.tags;

    return {
      employees: () => item.user.fullname,
      employeeId: () => item.user.employeeId || '-',
      positions: () => item.position.name,
      locations: () => item.location.name,
      groups: () => (groups.length ? groups.map((g) => g.name).join('\n') : '-'),
      tags: () => (tags.length ? item.tags.map((t) => t.name).join('\n') : '-'),
      shiftTime: () => item.shiftTimeLabel,
      status: () => this.RosterUtils.getEventStatus(item)?.title,
    }[field]();
  }

  async updateEvent(event) {
    const eventIndex = this.events.findIndex((e) => e.id === event.id);
    if (eventIndex < 0) {
      return;
    }

    this.events[eventIndex] = event;
    try {
      await this.refreshGroups();
    } catch (e) {
      this.initGroups();
    }
  }

  async _processEvents() {
    const date = this.dateString;
    const whoseEvents = this.RosterService.whoseEvents;

    await this.ShiftUtils.loadAsync();

    const days = [date, this.Utils.formatDate(this.Utils.addDays(date, 1))];
    return await this.ShiftUtils.processEvents({
      events: this.events,
      whoseEvents,
      filter: this.RosterService.filter,
      processingMode: this.processingMode,
      days,
      currentPeriodDays: days,
      users: this.eventUsers,
      sortMethod: this.RosterService.groupMethod,
      displayMode: 'day',
      showPlanningEvents: false,
      showForeignShifts: false,
      skipLabor: true,
      skipUnscheduled: false,
    });
  }

  getPrintPayload() {
    const props = ['columns', 'groups', 'events', 'tableOrderBy', 'shouldHideColumn'];

    const rosterCtrl = this.Utils.extendWithProps(this, {}, props);

    const authenticationUser = {
      settings: {
        usTimeFormat: this.Authentication.user.settings.usTimeFormat,
      },
      org: {
        isPremium: this.Authentication.user.org.isPremium,
      },
      hasAccessToLaborCost: this.Authentication.user.hasAccessToLaborCost,
      wouldHaveAccessToLaborCost: this.Authentication.user.wouldHaveAccessToLaborCost,
      orgSettings: { ...this.Authentication.user.orgSettings },
    };
    delete authenticationUser.orgSettings.shiftTemplates;

    const allPersonas = this.PersonaCollection.allPersonas.map((persona) =>
      this.Utils.extendWithProps(persona, {}, [
        'id',
        'type',
        'name',
        'color',
        'fullname',
        'employeeId',
      ])
    );

    const { tags, userGroups } = this.RosterService;
    return {
      rosterCtrl,
      authenticationUser,
      allPersonas,
      tags: this.PersonaCollection.tags,
      dayParts: this.DayPartsService.dayParts,
      rosterData: {
        tags,
        userGroups,
        tableData: this.RosterService.tableData,
        groupMethod: this.RosterService.groupMethod,
        date: this.dateFilter(this.RosterService.date, 'yyyy-MM-ddTHH:mm:00'),
        tableSortChain: this.RosterService.tableSortChain,
      },

      $route: 'roster',
      $replacer: this.rosterPrintPayloadReplacer,
    };
  }
}

export default {
  controller: RosterCtrl,
  controllerAs: 'ctrl',
  bindings: {},
  templateUrl: rosterUrl,
};
